import { UserRole } from "types/role"
import { TSentence, TViseme, TWord } from "types/speechMarks"

/**
 * The options used to configure apisauce.
 */
export interface ApiConfig {
  /**
   * The URL of the api.
   */
  url: string

  /**
   * Milliseconds before we timeout the request.
   */
  timeout: number
}

export interface LoginResponse {
  AuthenticationResult: {
    AccessToken: string
    ExpiresIn: number
    IdToken: string
    RefreshToken: string
    TokenType: string
  }
  message: string
  sponsor_id?: string
  sponsor_name?: string
  environment_name?: string
  environment_url?: string
  target_urls?: {
    sme_server_url?: string
    web_server_url?: string
  }
  /** Abbreviated role code from the backend login endpoint (e.g. "SM", "SA", "ME", "TM"). */
  role?: string
  default_role?: UserRole
  available_roles?: UserRole[]
  cognito_role?: string
}

export interface LoginResponseMultiSponsor {
  AuthenticationResult: {
    AccessToken: string
    ExpiresIn: number
    IdToken: string
    RefreshToken: string
    TokenType: string
  }
  message: string
  requires_sponsor_selection: true
  available_sponsors: Array<{
    sponsor_id: string
    sponsor_name?: string
    environment_name: string
    environment_url?: string
    target_urls: {
      sme_server_url?: string
      web_server_url?: string
    }
    /** Abbreviated role code from the backend login endpoint (e.g. "SM", "SA", "ME", "TM"). */
    role?: string
    default_role?: UserRole
    available_roles?: UserRole[]
    cognito_role?: string
  }>
  status: number
}

// Union type for both login response types
export type LoginResponseUnion = LoginResponse | LoginResponseMultiSponsor

// Type guard function to check if response requires sponsor selection
export function isMultiSponsorResponse(
  response: LoginResponseUnion,
): response is LoginResponseMultiSponsor {
  return "available_sponsors" in response
}

export interface LoginMeta {
  sponsorId?: string
  environmentName?: string
  targetUrls?: {
    smeServerUrl?: string
    webServerUrl?: string
  }
  requiresSponsorSelection: boolean
  /** Inflated sponsor list from the /login response, used by SelectSponsor without a network call. */
  sponsors: SponsorInfo[]
}

export interface RefreshResponse {
  AuthenticationResult: {
    AccessToken: string
    ExpiresIn: number
    IdToken: string
    TokenType: string
    // refresh token isn't returned in the response
  }
  message: string
}

export interface AuthTokens {
  AccessToken: string
  ExpiresIn: number
  ExpiresAt: number // ExpiresAt is not included in the data from server, but we need to store it
  IdToken: string
  RefreshToken: string
  TokenType: string
}

export interface TodoItemError {
  message: string
  has_error: boolean
}

export interface TodoItemSourceResource {
  resource_id: string
  type: string
  data: string
  title: string
  description: string
  file_name: string
  keywords: string[]
  updated_at: string
  updated_by: string
  resource_key: string
  is_approved: boolean
  show_in_app: boolean
  show_in_app_supported: boolean
  is_source_file: boolean
  resource_metadata: Record<string, unknown>
  media_length_seconds: number | null
  transcode_progress: Record<string, unknown>
  semantic_id: string
  playlist_s3_key: string | null
  thumbnail_s3_key: string | null
  youtube_id?: string | null
}

export interface TodoItem {
  task_id: string
  cm_id: string
  primary_step_id: string
  sponsor_ids: string[]
  name: string
  title: string
  short_description: string
  long_description: string
  select_esk: string
  demographic: string
  are_step_sequential: boolean
  visible_to_msk: boolean
  allowed_for_all_sponsors: boolean
  delivery_mode: number
  status: string
  created_at: string
  created_by: string
  updated_at: string
  updated_by: string
  in_process: boolean
  outline: string
  subscription_benefits: string
  presentation_style: string
  introduction: string
  error: TodoItemError
  is_media_mode: boolean
  semantic_id: string
  locked_by: Record<string, unknown>
  ai_generate_templates: Record<string, unknown>
  last_published_at?: string | null
  thumbnail_url: string | null
  media_type: ChannelMediaType
  media_url: string | null
  source_resource: TodoItemSourceResource | null
  topic_priority: number
  /** Opaque topic IDs (not human-readable) */
  topics?: string[]
  /** Human-readable topic/category display names — mirrors the unauth `topic` field */
  categories?: string[]
}

export interface LLMConfig {
  id: string
  type: string
  description: string
  user_facing_name: string
}

export interface LLMAvailableResponse {
  kind: "ok"
  llmConfig: LLMConfig[]
}

// Role-related types
export interface RoleInfoResponse {
  current_role: UserRole
  current_user_id: string
  available_roles: Array<{
    role: UserRole
    user_id: string
    is_active: boolean
  }>
  main_user_id: string
}

// Sponsor-related types
export interface SponsorInfo {
  sponsor_id: string
  sponsor_name: string
  environment_name: string
  environment_url: string
  default_role?: UserRole
  available_roles?: UserRole[]
  cognito_role?: string
}

export interface SponsorListResponse {
  sponsors: SponsorInfo[]
}

// Context lifecycle types (unified endpoints: /context/switch, /context/refresh, /context/reactivate)
export interface ContextSwitchRequest {
  sponsor_id: string
  is_login: boolean
  role?: UserRole
}

/** Response from POST /context/switch — trimmed to essential fields. Only context_token is consumed. */
export interface ContextSwitchResponse {
  context_token: string
  session_id: string
  sponsor_id: string
  message?: string
}

/** Response from POST /context/refresh (silent rotation). */
export interface ContextRefreshResponse {
  context_token: string
}

/** Response from POST /context/reactivate (Resume Here / displaced-tab reclaim). */
export interface ContextReactivateResponse {
  context_token: string
}

// Channel brand data (from GET /sponsors/current-sponsor or GET /unauth/channel_info/:id)
export interface ChannelBrandData {
  id: string
  name: string
  brand_image: string | null
  brand_title: string | null
  brand_subtitle: string | null
  brand_color: string | null
  /** Only returned by the unauthenticated /unauth/channel_info endpoint. */
  subscription_url?: string | null
  /** Whether the channel is a media-first (public content library) channel. Absent on older backends — treat absence as true. */
  is_media_mode?: boolean
}

// Channel media types
export type ChannelMediaType = "video" | "pdf" | "audio" | "image" | "url" | "youtube" | null

export interface ChannelMediaItem {
  media_type: ChannelMediaType
  title: string
  subscription_only: boolean
  thumbnail_url: string | null
  creation_time: string
  sidekick_name: string
  /** UUID, "PSK", or a numeric string */
  avatar_id: string
  cm_priority: number | null
  media_url: string | null
  topic?: string
  cm_id?: string
  task_id?: string
  last_published_at?: string | null
}

export interface UnauthPitchAudio {
  audio_url: string
  transcript: string
  duration_ms: number
}

/** Response from GET /intro_message/<channelId> */
export interface IntroMessageResponse {
  audio_url: string
  speech_marks: Array<TSentence | TWord | TViseme>
  avatar_id: string
  psk_name: string
}

// Feedback annotation tags
export interface AnnotationTag {
  id: string
  name: string
}

/** GET /session/validate when the exclusive session is still active */
export interface SessionValidateResponse {
  valid_session: boolean
  user_id: string
  user_email: string
  session_id: string
}

/** Item returned by GET /content/get_resources?task_id=<id> or ?step_id=<id>. */
export interface ContentResourceSummary {
  resource_id: string
  type: string
  data: string
  title: string
  description: string
  file_name: string
  keywords: string[]
  updated_at: string
  updated_by: string
  resource_key: string | null
  in_process?: boolean | null
  process_progress?: number | null
  source_url?: string | null
  is_approved: boolean
  show_in_app: boolean
  show_in_app_supported: boolean
  is_source_file: boolean
  timestamp?: string | null
  /** Signed relative path for the HLS playlist. Present for video-type resources. */
  playlist_s3_key?: string | null
  /** Signed relative path for the thumbnail image. Present for video-type resources. */
  thumbnail_s3_key?: string | null
  youtube_id?: string | null
  title_ai?: string | null
  description_ai?: string | null
  media_length_seconds: number
  transcode_progress?: {
    is_processing: boolean
    percent: number
    status: string
    job_id: string
  } | null
  resource_metadata?: Record<string, unknown> | null
  semantic_id?: string | null
}
